import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  output,
  signal,
} from "@angular/core";
import { unByKey } from "ol/Observable";
import { EventsKey } from "ol/events";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateModule } from "@ngx-translate/core";
import { BehaviorSubject, forkJoin, Subject, takeUntil } from "rxjs";
import proj4 from "proj4";
import { register } from "ol/proj/proj4";
import { transform } from "ol/proj";
import { Graticule, Map, View } from "ol";
import { Stroke } from "ol/style";
import Fill from "ol/style/Fill";
import OLText from "ol/style/Text";

import { UtilisateurService } from "src/app/services/utilisateur.service";
import { HttpService } from "src/app/services/http.service";
import { LoggerService } from "src/app/services/logger.service";
import {
  Blast,
  HoleService,
  HoleStatus,
  HoleTypeQuality,
} from "src/app/services/hole.service";
import { Site } from "src/app/bo/site";
import { TirsPolygoneEnum } from "src/app/bo/polygon/tirPolygoneEnum";
import MapConstants from "../../../../components/carte/constants";
import { BlastPolygonsLayer } from "../../../../components/carte/layers/BlastPolygoneLayer";
import { HoleLayer } from "../../../../components/carte/layers/holeLayer";
import { CarteHelpers } from "../../../../components/carte/carteHelpers";
import { CartStyleUtile } from "src/app/utils/carteStyleUtile";
import { BlastDesignDashboardComponent } from "../blast-design-dashboard/blast-design-dashboard.component";
import { SiteHoleTolerance, getThreshold } from "src/app/bo/site-hole-tolerance";

export enum FilterType {
  State = "STATE",
  Quality = "QUALITY",
  Dates = "DATES",
}

export enum QualityTypes {
  AsDesigned = "AS_DESIGNED",
  InTolerance = "IN_TOLERANCE",
  OutOfSpec = "OUT_OF_SPEC",
}

interface FilterOption {
  id: FilterType;
  nom: string;
}

interface QualityOption {
  key: HoleTypeQuality;
  label: string;
  unit: string;
}

interface QualityColorOption {
  color: string;
  type: QualityTypes;
}

interface HoleData {
  holeName: string;
  head: number;
  length: number;
  toe: number;
  date: string;
}

@Component({
  selector: "app-blast-design-data",
  templateUrl: "./blast-design-data.component.html",
  styleUrls: ["./blast-design-data.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslateModule, NgSelectModule, FormsModule],
})
export class BlastDesignDataComponent implements OnDestroy {
  // Inputs/Outputs
  readonly blast = input<Blast | null>(null);
  readonly qualityData = input<SiteHoleTolerance | null>(null);
  readonly selectedHoleId = input<string | null>(null);
  readonly holeSelected = output<{ holeId: string | null; changeCollapsed: boolean }>();

  // Map related properties
  site: Site;
  map: Map;
  view = new View();
  firstHolePosition: [number, number] = [0, 0];
  private blastPolygoneLayer: BlastPolygonsLayer;
  protected holeLayer: HoleLayer;
  graticule: Graticule;
  readonly groups: (HoleStatus | "actual")[][] = [];

  // Filter and display state — signals
  protected readonly selectedHoleStatusFilter = signal<string | HoleStatus>("actual");
  protected readonly holesAreShown = signal(false);
  protected readonly listeTirsSelectionnes = signal<number[]>([]);
  readonly listeTypesSelectionnes: number[];

  // Tab state — signals
  protected readonly topTab = signal<"design" | "dashboard">("design");
  protected readonly viewTab = signal<"2d" | "3d">("2d");

  // Filter configuration — signals
  public FilterType = FilterType;
  protected readonly selectedType = signal<FilterType>(FilterType.State);
  protected readonly qualitySelected = signal("HEAD");
  protected readonly dateSelected = signal<string[]>([]);
  protected readonly dates = signal<string[]>([]);
  protected readonly holeDataList = signal<HoleData[]>([]);

  protected readonly areAllSelected = computed(() =>
    this.dates().length > 0 && this.dates().every(d => this.dateSelected().includes(d))
  );

  // Static data arrays
  readonly qualities: QualityOption[] = [
    { key: HoleTypeQuality.Head, label: "HEAD_XY", unit: 'cm' },
    { key: HoleTypeQuality.Length, label: "LENGTH", unit: 'm' },
    { key: HoleTypeQuality.Toe, label: "TOE_XY", unit: 'cm' },
  ];

  readonly qualitiesColor: QualityColorOption[] = [
    { color: "green", type: QualityTypes.AsDesigned },
    { color: "yellow", type: QualityTypes.InTolerance },
    { color: "red", type: QualityTypes.OutOfSpec },
  ];

  readonly types: FilterOption[] = [
    { id: FilterType.State, nom: "STATE" },
    { id: FilterType.Quality, nom: "QUALITY" },
    { id: FilterType.Dates, nom: "DATES" },
  ];

  // Internal state
  private readonly unsubscribe$ = new Subject<void>();
  private proj4Site = "";
  private refreshHolesTimeout: any;
  private resolutionListenerKey: EventsKey | null = null;
  private readonly LABEL_HIDE_RESOLUTION = 0.1;

  protected readonly cartSelectedTirsPolygoneEnum: TirsPolygoneEnum[] = [
    TirsPolygoneEnum.DRILLED,
  ];

  readonly isMapLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly isMapLoading = toSignal(this.isMapLoading$, { initialValue: false });

  constructor(
    protected userService: UtilisateurService,
    private readonly http: HttpService,
    private readonly holeService: HoleService,
    private readonly logger: LoggerService
  ) {
    this.listeTypesSelectionnes = this.cartSelectedTirsPolygoneEnum.map((x) =>
      CarteHelpers.enumToNumber(x)
    );

    this.holeStateDesignGroups();

    effect(() => {
      const b = this.blast();
      if (!b?.BlastDesign?.length) return;
      this.isMapLoading$.next(true);
      this.selectedHoleStatusFilter.set("actual");
      this.holeDataList.set(this.createHoleDataList(b));
      const d = this.getDates(b);
      this.dates.set(d);
      this.dateSelected.set([...d]);
      this.firstHolePosition = [b.BlastDesign[0].PositionPlannedXYZ.x, b.BlastDesign[0].PositionPlannedXYZ.y];
      this.listeTirsSelectionnes.set([b.tirId]);
      if (this.map && this.site) {
        this.repositionMapAndRefresh();
      } else {
        this.initMap();
      }
    });

    effect(() => {
      const holeId = this.selectedHoleId();
      if (this.holeLayer && this.blast()?.BlastDesign) {
        this.selectHoleOnMap(holeId);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.refreshHolesTimeout) {
      clearTimeout(this.refreshHolesTimeout);
    }

    if (this.resolutionListenerKey) {
      unByKey(this.resolutionListenerKey);
      this.resolutionListenerKey = null;
    }

    if (this.map) {
      this.map.setTarget(null);
    }
  }

  private initMap() {
    this.site = this.userService.currentSite();
    this.onChangeSite();
  }

  private repositionMapAndRefresh() {
    if (!this.map || !this.site || !this.firstHolePosition) {
      return;
    }

    if (this.refreshHolesTimeout) {
      clearTimeout(this.refreshHolesTimeout);
    }

    this.clearMapLayers();

    const coordTrous = transform(
      [this.firstHolePosition[0], this.firstHolePosition[1]],
      "EPSG:" + this.site.epsg,
      MapConstants.EPSG_REFERENCE_OL
    );

    this.view.setCenter(coordTrous);
    this.view.setZoom(20);

    requestAnimationFrame(() => {
      this.holesAreShown.set(true);

      this.blastPolygoneLayer = new BlastPolygonsLayer(
        this.map,
        this.site.siteId,
        this.http,
        20
      );

      this.holeLayer = new HoleLayer(
        this.map,
        this.holeService,
        this.logger,
        30
      );
      this.holeLayer.setShowHoleNames(true);
      this.configureZoomForLabels();

      this.DisplayBlastPolygons(this.listeTirsSelectionnes());

      requestAnimationFrame(() => {
        this.RefreshHoles();
      });
    });
  }

  onChangeSite() {
    forkJoin([this.http.get<string>("Sites/proj4/" + this.site.epsg)])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resultat) => {
        this.proj4Site = resultat[0];
        this.registerProjString(this.proj4Site);

        this.createMap();

        this.blastPolygoneLayer = new BlastPolygonsLayer(
          this.map,
          this.site.siteId,
          this.http,
          20
        );

        this.holeLayer = new HoleLayer(
          this.map,
          this.holeService,
          this.logger,
          30
        );

        this.holeLayer.setShowHoleNames(true);

        this.onClick();

        this.map.addLayer(this.createGraticule());
        this.addWhiteSquare();

        this.configureOnPan();
        this.configureZoomForLabels();
        this.holesAreShown.set(true);
        this.DisplayBlastPolygons(this.listeTirsSelectionnes());

        setTimeout(() => {
          this.RefreshHoles();
          setTimeout(() => {
            this.isMapLoading$.next(false);
          }, 300);
        }, 500);
      });
  }

  private createMap() {
    const coordTrous = transform(
      [this.firstHolePosition[0], this.firstHolePosition[1]],
      "EPSG:" + this.site.epsg,
      MapConstants.EPSG_REFERENCE_OL
    );

    if (this.view) {
      this.view.setCenter(coordTrous);
      this.view.setZoom(20);
    } else {
      this.view = new View({
        center: coordTrous,
        zoom: 20,
      });
    }

    if (this.map) {
      this.map.setView(this.view);
    } else {
      this.map = new Map({
        layers: null,
        target: "map-design",
        view: this.view,
        controls: [],
      });
    }
  }

  private configureOnPan() {
    this.map.on("moveend", () => {
      if (this.refreshHolesTimeout) {
        clearTimeout(this.refreshHolesTimeout);
      }
      this.refreshHolesTimeout = setTimeout(() => {
        this.RefreshHoles();
      }, 100);
    });
  }

  private configureZoomForLabels(): void {
    if (this.resolutionListenerKey) {
      unByKey(this.resolutionListenerKey);
    }
    this.resolutionListenerKey = this.map.getView().on('change:resolution', () => {
      if (!this.holeLayer) return;
      const resolution = this.map.getView().getResolution();
      this.holeLayer.setShowHoleNames(resolution <= this.LABEL_HIDE_RESOLUTION);
    }) as EventsKey;

    // Apply immediately so labels reflect the current zoom level
    if (this.holeLayer) {
      const resolution = this.map.getView().getResolution();
      this.holeLayer.setShowHoleNames(resolution <= this.LABEL_HIDE_RESOLUTION);
    }
  }

  private createGraticule(): Graticule {
    this.graticule = new Graticule({
      targetSize: 60,
      showLabels: true,
      strokeStyle: new Stroke({ color: "rgba(126, 124, 124, 0.36)", width: 1 }),
      lonLabelFormatter: (lon) => this.formatLonLabel(lon),
      latLabelFormatter: (lat) => this.formatLatLabel(lat),
      lonLabelPosition: 0.15,
      latLabelPosition: 0.055,
      lonLabelStyle: new OLText({
        font: "12px Roboto",
        fill: new Fill({ color: "#5A5A5A" }),
      }),
      latLabelStyle: new OLText({
        font: "12px Roboto",
        fill: new Fill({ color: "#5A5A5A" }),
      }),
    });

    return this.graticule;
  }

  private formatLonLabel(lon: number): string {
    const viewCenter = this.map.getView().getCenter();
    const viewCenterInWGS84 = transform(
      viewCenter,
      this.map.getView().getProjection(),
      "EPSG:4326"
    );
    const pointInSiteProjection = transform(
      [lon, viewCenterInWGS84[1]],
      "EPSG:4326",
      "EPSG:" + this.site.epsg
    );

    if (this.isLabelInWhiteSquareArea(lon, true)) {
      return "";
    }

    return `${pointInSiteProjection[0].toFixed(0)}`;
  }

  private formatLatLabel(lat: number): string {
    const viewCenter = this.map.getView().getCenter();
    const viewCenterInWGS84 = transform(
      viewCenter,
      this.map.getView().getProjection(),
      "EPSG:4326"
    );
    const pointInSiteProjection = transform(
      [viewCenterInWGS84[0], lat],
      "EPSG:4326",
      "EPSG:" + this.site.epsg
    );

    if (this.isLabelInWhiteSquareArea(lat, false)) {
      return "";
    }

    return `${pointInSiteProjection[1].toFixed(0)}`;
  }

  private registerProjString(projString: string) {
    if (undefined !== projString && null !== projString && projString != "") {
      proj4.defs("EPSG:" + this.site.epsg, projString);
      register(proj4);
    }
  }

  private DisplayBlastPolygons(blastIdToDisplay?: number[]) {
    this.blastPolygoneLayer
      .displayPolygonsForBlast(
        blastIdToDisplay,
        this.listeTypesSelectionnes,
        false,
        4
      )
      .finally(() => {});
  }

  private RefreshHoles(): void {
    console.log('BlastDesignData: RefreshHoles called');
    console.log('Current listeTirsSelectionnes:', this.listeTirsSelectionnes());
    console.log('Current blast.tirId:', this.blast()?.tirId);

    const viewExtent = this.map.getView().calculateExtent(this.map.getSize());
    const visibleBlasts = this.blastPolygoneLayer.findPolygoneInView(viewExtent);

    let blastIdsInView = this.getUniqueBlastIds(visibleBlasts);
    console.log('Blast IDs in view before selection:', blastIdsInView);

    if (this.listeTirsSelectionnes().length > 0) {
      for (const selectedBlastId of this.listeTirsSelectionnes()) {
        if (!blastIdsInView.includes(selectedBlastId)) {
          blastIdsInView.push(selectedBlastId);
        }
      }
    }

    console.log('Final blast IDs to refresh:', blastIdsInView);
    console.log('Selected blast IDs:', this.listeTirsSelectionnes());

    this.updateQualityFilter();

    this.holeLayer.refreshHoles(
      blastIdsInView,
      this.holesAreShown(),
      this.listeTirsSelectionnes(),
      this.getVisibleHoleStatuses(),
      this.selectedHoleStatusFilter() === "actual"
        ? ("actual" as any)
        : (this.selectedHoleStatusFilter() as HoleStatus),
      null,
      this.blast(),
      this.isMapLoading$,
      true,
      false
    );
  }

  private getUniqueBlastIds(visibleBlasts: any[]): number[] {
    const blastIds: number[] = [];
    for (const feature of visibleBlasts) {
      const blastId = feature.get(MapConstants.TIR_ID).value;
      if (!blastIds.includes(blastId)) {
        blastIds.push(blastId);
      }
    }
    return blastIds;
  }

  protected updateHoleVisibility() {
    if (this.holesAreShown()) {
      const visibleStatuses = this.getVisibleHoleStatuses();
      this.updateQualityFilter();

      this.holeLayer.refreshHoleVisibility(
        visibleStatuses,
        this.selectedHoleStatusFilter() === "actual"
          ? ("actual" as any)
          : (this.selectedHoleStatusFilter() as HoleStatus)
      );
    } else {
      this.holeLayer.hideAllHoles();
    }
  }

  private getVisibleHoleStatuses(): Set<HoleStatus> {
    if (this.selectedHoleStatusFilter() === "actual") {
      return new Set(this.getHoleStatusDesign());
    } else {
      return new Set([this.selectedHoleStatusFilter() as HoleStatus]);
    }
  }

  protected onHoleStatusFilterChange(filter: string | HoleStatus) {
    this.selectedHoleStatusFilter.set(filter);
    this.updateHoleVisibility();
  }

  protected holeStateDesignGroups() {
    const statuses = this.getHoleStatusDesign();
    const withActual: Array<HoleStatus | "actual"> = ["actual", ...statuses];

    for (let i = 0; i < withActual.length; i += 3) {
      this.groups.push(withActual.slice(i, i + 3));
    }
  }

  private getHoleStatusDesign(): HoleStatus[] {
    const excludedStatuses = new Set([
      HoleStatus.UNSET,
      HoleStatus.LOADED,
      HoleStatus.LOADING,
      HoleStatus.BLASTED,
    ]);

    return Object.values(HoleStatus).filter(
      (status) => !excludedStatuses.has(status)
    );
  }

  protected getHoleStatusIcon(status: HoleStatus | "actual"): string {
    if (status === "actual") {
      return "assets/icon/all_holes.svg";
    }
    return this.holeLayer.getHoleIconPath(status as string);
  }

  private addWhiteSquare(): void {
    setTimeout(() => {
      const mapContainer = document.getElementById("map-design");
      if (mapContainer) {
        const whiteSquare = document.createElement("div");
        whiteSquare.style.position = "absolute";
        whiteSquare.style.bottom = "0px";
        whiteSquare.style.left = "0px";
        whiteSquare.style.width = "50px";
        whiteSquare.style.height = "50px";
        whiteSquare.style.backgroundColor = "white";
        whiteSquare.style.zIndex = "1000";
        whiteSquare.style.pointerEvents = "none";

        mapContainer.appendChild(whiteSquare);
      }
    }, 100);
  }

  private isLabelInWhiteSquareArea(
    coordinateValue: number,
    isLongitude: boolean
  ): boolean {
    if (!this.map) return false;

    const mapSize = this.map.getSize();
    if (!mapSize) return false;

    const view = this.map.getView();
    const extent = view.calculateExtent(mapSize);

    const whiteSquareWidthPixels = 50;
    const whiteSquareHeightPixels = 50;

    const resolution = view.getResolution();
    if (!resolution) return false;

    const whiteSquareWidthUnits = whiteSquareWidthPixels * resolution;
    const whiteSquareHeightUnits = whiteSquareHeightPixels * resolution;

    if (isLongitude) {
      const leftBoundary = extent[0];
      const whiteSquareRightBoundary = leftBoundary + whiteSquareWidthUnits;

      const viewCenter = this.map.getView().getCenter();
      if (!viewCenter) return false;

      const viewCenterInWGS84 = transform(
        viewCenter,
        view.getProjection(),
        "EPSG:4326"
      );
      const pointInViewProjection = transform(
        [coordinateValue, viewCenterInWGS84[1]],
        "EPSG:4326",
        view.getProjection()
      );

      return pointInViewProjection[0] <= whiteSquareRightBoundary;
    } else {
      const bottomBoundary = extent[1];
      const whiteSquareTopBoundary = bottomBoundary + whiteSquareHeightUnits;

      const viewCenter = this.map.getView().getCenter();
      if (!viewCenter) return false;

      const viewCenterInWGS84 = transform(
        viewCenter,
        view.getProjection(),
        "EPSG:4326"
      );
      const pointInViewProjection = transform(
        [viewCenterInWGS84[0], coordinateValue],
        "EPSG:4326",
        view.getProjection()
      );

      return pointInViewProjection[1] <= whiteSquareTopBoundary;
    }
  }

  private onClick(): void {
    this.map.on("click", (evt) => {
      const features = this.map.getFeaturesAtPixel(evt.pixel);

      if (!features || features.length === 0) {
        return;
      }

      const holeFeature = features.find(
        (feature) =>
          feature.get(MapConstants.FEATURE_NAME) ===
          MapConstants.FEATURE_TYPE.hole
      );

      if (holeFeature) {
        const hole = holeFeature.get(MapConstants.HOLE_OBJECT);
        if (hole) {
          this.selectHole(hole);
        }
      }
    });
  }

  private selectHole(hole: any): void {
    this.holeLayer.setSelectedHole(hole.name);
    this.holeLayer.refreshHoleStyles();
    this.holeSelected.emit({ holeId: hole.publicGuid, changeCollapsed: false });
  }

  private selectHoleOnMap(holeId: string | null): void {
    if (!this.holeLayer || !this.blast()?.BlastDesign) {
      return;
    }

    if (!holeId) {
      this.holeLayer.setSelectedHole(null);
      this.holeLayer.refreshHoleStyles();
      return;
    }

    const hole = this.blast().BlastDesign.find(h => h.publicGuid === holeId);
    if (hole) {
      this.holeLayer.setSelectedHole(hole.name);
      this.holeLayer.refreshHoleStyles();

      this.centerMapOnHole(hole);
    }
  }

  private centerMapOnHole(hole: any): void {
    if (!this.map || !this.site || !hole.PositionPlannedXYZ) {
      return;
    }

    const coordTrous = transform(
      [hole.PositionPlannedXYZ.x, hole.PositionPlannedXYZ.y],
      "EPSG:" + this.site.epsg,
      MapConstants.EPSG_REFERENCE_OL
    );

    this.view.animate({
      center: coordTrous,
      duration: 500
    });
  }

  onTypeChange(value: FilterType | null) {
    this.selectedType.set(value);
    this.updateQualityFilter();
    this.RefreshHoles();
  }

  onQualitySelectedChange(value: string) {
    this.qualitySelected.set(value);
    if (this.selectedType() === FilterType.Quality) {
      this.RefreshHoles();
    }
  }

  private updateQualityFilter() {
    if (this.selectedType() === FilterType.Quality && this.holeLayer) {
      const qualityFilterInfo = {
        selectedMetric: this.qualitySelected(),
        holeDataList: this.holeDataList(),
        tolerance: this.qualityData(),
      };
      this.holeLayer.setQualityFilter(qualityFilterInfo);
      this.holeLayer.setDateFilter(null);
    } else if (this.selectedType() === FilterType.Dates && this.holeLayer) {
      const dateFilterInfo = {
        selectedDates: this.dateSelected(),
        holeDataList: this.holeDataList(),
        allDates: this.dates(),
      };
      this.holeLayer.setDateFilter(dateFilterInfo);
      this.holeLayer.setQualityFilter(null);
    } else if (this.holeLayer) {
      this.holeLayer.setQualityFilter(null);
      this.holeLayer.setDateFilter(null);
    }
  }

  protected getIconHoleQuality(type: HoleTypeQuality): string {
    return CartStyleUtile.iconHoleQuality(type as string);
  }

  onCheckboxChange(event: Event, date: string) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.dateSelected.update(d => [...d, date]);
    } else {
      this.dateSelected.update(d => d.filter(x => x !== date));
    }

    if (this.selectedType() === FilterType.Dates) {
      this.updateQualityFilter();
      this.RefreshHoles();
    }
  }

  getDateColorForHole(holeName: string): string | null {
    const holeData = this.holeDataList().find((h) => h.holeName === holeName);
    if (!holeData) {
      return null;
    }

    const dateIndex = this.dates().indexOf(holeData.date);
    if (dateIndex === -1) {
      return null;
    }

    const dayColors = [
      "#FF7377",
      "#8661AE",
      "#FAC70F",
      "#0DC7FF",
      "#4870D5",
      "#A10FC5",
      "#1CC254",
      "#CB91AD",
      "#EE8A19",
    ];

    return dayColors[dateIndex] || null;
  }

  private createHoleDataList(blast: Blast): HoleData[] {
    return blast.BlastDesign.map((hole) => {
      let length: number | null = null;
      if (hole.Geometry?.Measure?.length != null && hole.Geometry?.Design?.length != null) {
        length = Math.abs(hole.Geometry.Measure.length - hole.Geometry.Design.length);
      }
      else if (hole.Geometry?.drilllength != null && hole.Geometry?.Design?.length != null) {
        length = Math.abs(hole.Geometry.drilllength - hole.Geometry.Design.length);
      }
      return {
        holeName: hole.name || "Unknown",
        head: hole.calculatedValues?.headPositionMagnitude,
        length: length,
        toe: hole.calculatedValues?.toePositionMagnitude,
        date: this.formatDate(hole.drillingDate) || "",
      };
    });
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split("/").map(Number);
    return new Date(year, month - 1, day);
  }

  getTextQualityType(
    type: QualityTypes,
    holeType: HoleTypeQuality
  ): string {
    const qualityConfig = this.getQualityConfig(holeType);
    const qualityValue = this.getQualityValue(type, qualityConfig);

    return ` ${qualityValue}`;
  }

  private getQualityConfig(holeType: HoleTypeQuality) {
    const qd = this.qualityData();
    const configMap = {
      [HoleTypeQuality.Head]: {
        good: getThreshold(qd, 'HEAD')?.asDesignedMax,
        bad: getThreshold(qd, 'HEAD')?.inToleranceMax,
      },
      [HoleTypeQuality.Length]: {
        good: getThreshold(qd, 'LENGTH')?.asDesignedMax,
        bad: getThreshold(qd, 'LENGTH')?.inToleranceMax,
      },
      [HoleTypeQuality.Toe]: {
        good: getThreshold(qd, 'TOE_POSITION')?.asDesignedMax,
        bad: getThreshold(qd, 'TOE_POSITION')?.inToleranceMax,
      },
    };

    return configMap[holeType] || { good: 0, bad: 0 };
  }

  private getQualityValue(
    type: QualityTypes,
    config: { good: number; bad: number }
  ): string {
    switch (type) {
      case QualityTypes.AsDesigned:
        return `<${config.good}`;
      case QualityTypes.InTolerance:
        return `${config.good} - ${config.bad}`;
      case QualityTypes.OutOfSpec:
        return `>${config.bad}`;
      default:
        return "Unknown";
    }
  }

  private clearMapLayers() {
    if (!this.map) return;

    if (this.holeLayer) {
      this.holeLayer.hideAllHoles();
      this.holeLayer.clear();
    }

    const layers = this.map.getLayers().getArray().slice();
    layers.forEach(layer => {
      if (layer.get('name') !== 'graticule' &&
          layer !== this.graticule &&
          layer.get('type') !== 'base') {
        this.map.removeLayer(layer);
      }
    });

    if (this.blastPolygoneLayer) {
      this.blastPolygoneLayer = null;
    }
    if (this.holeLayer) {
      this.holeLayer = null;
    }

    this.map.renderSync();
  }

  onSelectAllChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.dateSelected.set([...this.dates()]);
    } else {
      this.dateSelected.set([]);
    }
    this.updateQualityFilter();
    this.RefreshHoles();
  }

  getDateColor(dateKey: string): string {
    return HoleLayer.getColorForKey(dateKey);
  }

  getDates(blast: Blast): string[] {
    return Array.from(
      new Set(
        blast.BlastDesign
          .map(h => h.drillingDate)
          .filter((date): date is string => !!date)
          .map(date => this.formatDate(date))
      )
    ).sort((a, b) => this.parseDate(a).getTime() - this.parseDate(b).getTime());
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);

    const isValidDate =
      date instanceof Date && !isNaN(date.getTime());

    if (!isValidDate) {
      return '';
    }

    return date.toLocaleDateString('fr-FR');
  }
}
